import { useMutationState, type QueryClient } from '@tanstack/react-query';
import { liveHostKey, liveScreenLookMutationKey } from '@lib/cache/polls';
import type { LiveHostState, LiveScreenLook } from '@models/api';
import { DEFAULT_LIVE_SCREEN_LOOK, lookOf } from './liveScreenLook';

/**
 * The big screen's look as the host sees it: the newest save still on its way to the server (`useSaveLiveScreenLook`),
 * else the look the host read holds. The saves are drawn from here rather than written into the host read, because
 * that read refreshes every 2 s from several screens at once and one landing mid-save would put the old look back
 * until the save returned. Every save carries the whole look and they go out in tap order, so the last one still
 * pending (a queued one counts) is the host's latest tap. Only the host's own screens read this; the room's screens
 * show what the server has.
 */
export function useHostScreenLook(code: string, cachedLook: LiveScreenLook): LiveScreenLook {
  const pending = useMutationState({
    filters: { mutationKey: liveScreenLookMutationKey(code), status: 'pending' },
    select: mutation => mutation.state.variables as LiveScreenLook,
  });
  return pending.at(-1) ?? cachedLook;
}

/**
 * `useHostScreenLook`'s answer read straight from the caches at the moment of a tap: the newest save still pending,
 * else the host read's look. A save merges into this, never into a look a render captured, so two taps inside one
 * React update both land.
 */
export const newestHostScreenLook = (queryClient: QueryClient, code: string): LiveScreenLook => {
  const pending = queryClient
    .getMutationCache()
    .findAll({ mutationKey: liveScreenLookMutationKey(code), status: 'pending' })
    .at(-1);
  if (pending) return pending.state.variables as LiveScreenLook;
  const host = queryClient.getQueryData<LiveHostState>(liveHostKey(code));
  return host ? lookOf(host.state) : DEFAULT_LIVE_SCREEN_LOOK;
};
